import { useDispatch, useSelector } from 'react-redux'
import {
  adjustPriceOnOrderItem,
  orderDetailsRequested,
  priceAdjustmentFailed,
  priceAdjustmentRequested,
  priceAdjustmentSucceeded,
  updateOrderTotals,
} from '../../actions/order'
import { PRICE_ADJUST_REASON } from '../consts'
import { getCartItemsFromOrder, getTaxByItem, getTaxRate } from '../../selectors/cart'
import { getMenuServiceCharge } from '../../selectors/items'
import {
  calculateIndividualTaxAmount,
  calculateServiceCharge,
  calculateServiceFeeTaxRules,
  calculateSubtotal,
  calculateTax,
} from '../../utils/totalUtils'
import { getUseNormalTaxRounding, getUseTaxClasses } from '../../selectors/menus'
import { updateLineItems } from '../Apis'
import { retotalOrder } from '../../VNReopenTab/Apis'
import { ToastManager } from '../../utils/toastManager'
import { getTaxClasses, getTaxRules } from '../../VNTaxes/Selectors'
import { getOrderInProgress } from '../../selectors/order'

const usePriceAdjustment = (orderUuid = null) => {
  const dispatch = useDispatch()

  const taxRate = useSelector(getTaxRate)
  const taxByItem = useSelector(getTaxByItem)
  const serviceCharge = useSelector(getMenuServiceCharge)
  const useNormalTaxRounding = useSelector(getUseNormalTaxRounding)
  const taxClasses = useSelector(getTaxClasses)
  const taxRules = useSelector(getTaxRules)
  const useTaxClasses = useSelector(getUseTaxClasses)
  const orderInProgress = useSelector(getOrderInProgress)

  /**
   * Creates mutation payload for updating line items
   * @param {string} menuItemUuid uuid for the menu item, this is fixed per item
   * @param {array} lineItemUuids uuid for the order item
   * @param {number} priceInCents new price
   * @returns updateLineItems mutation payload
   */
  const mapOrderMenuItems = (menuItemUuid, lineItemUuids, priceInCents, managerId) => {
    return lineItemUuids.map((lineItemUuid) => {
      return {
        menuItemUuid,
        uuid: lineItemUuid,
        price: priceInCents / 100,
        quantity: 1, //fixed since we're keeping quantity = 1 in the order
        reason: PRICE_ADJUST_REASON,
        approverUuid: managerId,
      }
    })
  }

  const adjustPriceOnline = async ({ menuItemUuid, lineItemUuids, priceInCents, managerId }) => {
    const orderMenuItems = mapOrderMenuItems(menuItemUuid, lineItemUuids, priceInCents, managerId)

    try {
      dispatch(priceAdjustmentRequested())
      await updateLineItems(orderUuid, orderMenuItems, PRICE_ADJUST_REASON, managerId)

      //retotal order in stadium
      await retotalOrder(orderUuid)

      //get updated values from stadium
      await dispatch(orderDetailsRequested(orderUuid))

      dispatch(priceAdjustmentSucceeded())
      ToastManager.success('Success! Price adjusted.')
    } catch (error) {
      dispatch(priceAdjustmentFailed())
      ToastManager.error(error?.message || 'Failed to adjust price.')
    }
  }

  const adjustPriceOffline = ({ lineItemUuids, priceInCents, originalPriceInCents, managerId }) => {
    lineItemUuids.forEach((lineItemUuid) => {
      const price = priceInCents / 100
      const formattedPrice = `$${price.toFixed(2)}`

      dispatch(
        adjustPriceOnOrderItem({
          orderUuid,
          lineItemUuid,
          price,
          formattedPrice,
          priceInCents,
          originalPriceInCents,
          managerId,
          reason: PRICE_ADJUST_REASON,
        }),
      )
    })

    //since the dispatch of adjustPriceOnOrderItem won't trigger a rerender using selector getCartItemsFromOrder at a hook level
    //is not going to reflect the updated items, so we need to call this selector right before retotalling
    dispatch((_, getState) => {
      const lineItems = getCartItemsFromOrder(getState(), orderUuid)
      retotalOfflineOrderWithUpdate(lineItems)
    })

    dispatch(priceAdjustmentSucceeded())
    ToastManager.success('Success! Price adjusted.')
  }

  const retotalOfflineOrderWithUpdate = (lineItems) => {
    const updatedOrder = retotalOfflineOrder(lineItems)

    dispatch(updateOrderTotals(updatedOrder))
  }

  const retotalOfflineOrder = (lineItems) => {
    const adjustedLineItems = lineItems.map((item) => {
      return {
        ...item,
        defaultPriceInCents: item.priceInCents,
      }
    })

    const subtotalAmountInCents = calculateSubtotal(adjustedLineItems)

    const serviceFee = {
      name: serviceCharge.name,
      total: calculateServiceCharge(serviceCharge, subtotalAmountInCents) ?? 0,
      legal: serviceCharge.legal,
      taxClassUuid: serviceCharge?.taxClassUuid,
    }

    const calculateTaxResult = calculateTax(
      adjustedLineItems,
      taxRate,
      taxByItem,
      useNormalTaxRounding,
      useTaxClasses,
      taxClasses,
      taxRules,
      orderInProgress.taxExempt || false,
    )

    let taxRulesBreakdown = calculateTaxResult.taxRulesBreakdown || {}

    let serviceFeeTaxAmountInCents = 0

    if (useTaxClasses) {
      const calculateServiceFeeTaxRulesResult = calculateServiceFeeTaxRules(
        taxRulesBreakdown,
        serviceFee,
        useNormalTaxRounding,
        taxClasses,
        taxRules,
        orderInProgress.taxExempt || false,
      )
      serviceFeeTaxAmountInCents = calculateServiceFeeTaxRulesResult.serviceFeeTaxAmountInCents
      taxRulesBreakdown = calculateServiceFeeTaxRulesResult.taxRulesBreakdown || {}
    } else {
      serviceFeeTaxAmountInCents = calculateIndividualTaxAmount(
        taxRate,
        serviceFee.total,
        serviceCharge.taxable,
        useNormalTaxRounding,
      )
    }

    const taxAmountInCents = calculateTaxResult.taxAmountInCents + serviceFeeTaxAmountInCents

    const amountInCents = subtotalAmountInCents + taxAmountInCents + serviceFee.total
    const balanceDueInCents = amountInCents

    return {
      orderId: orderUuid,
      subtotalAmountInCents,
      taxAmountInCents,
      amountInCents,
      balanceDueInCents,
      serviceFee,
      taxRulesBreakdown,
    }
  }

  return {
    adjustPriceOnline,
    adjustPriceOffline,
    retotalOfflineOrder,
  }
}

export default usePriceAdjustment
